/**
 * Seating and lying capacity, derived from the layout.
 *
 * "How many people fit?" used to be unanswerable — the app had no object to
 * count. Now it falls out of the bench runs: seats from total usable run
 * length, lying places from runs that are both long and deep enough for an
 * adult to stretch out on.
 *
 * Only the upper tier counts as a seat. The lower run is a foot bench — in a
 * Finnish layout you rest your feet on it, you do not seat a second row of
 * bathers there, and counting it would roughly double every number.
 */
import { getRuleValue, type RuleOverrides } from "../../data/ruleRegistry";
import { resolveEffectiveLayout } from "./effectiveRuns";
import type { HeaterSizeMm, InteriorLayout, InteriorRoom } from "./types";

export interface SeatingCapacityResult {
	/** People who can sit, at the per-person seat width rule. */
	seats: number;
	/** People who can lie down full-length. */
	lyingSpots: number;
	/** Total usable seating run length across all upper-tier runs, mm. */
	seatingLengthMm: number;
	/** Longest single uninterrupted upper-tier run, mm. */
	longestRunMm: number;
	/** Resolve at the render boundary with `{seats}` / `{lyingSpots}`. */
	summaryKey: "domain_interior_capacity_summary";
	args: { seats: number; lyingSpots: number };
}

export interface SeatingCapacityInput {
	layout: InteriorLayout;
	room: InteriorRoom;
	/** Omit for a room with no heater — nothing gets clipped. */
	heaterSize?: HeaterSizeMm | null;
}

export function calculateSeatingCapacity(
	input: SeatingCapacityInput,
	overrides: RuleOverrides = {},
): SeatingCapacityResult {
	const seatWidthMm = getRuleValue<number>(
		"ergonomics.seatWidthPerPersonMm",
		overrides,
	);
	const lyingLengthMm = getRuleValue<number>(
		"ergonomics.lyingLengthMinMm",
		overrides,
	);
	const lyingDepthMm = getRuleValue<number>(
		"ergonomics.lyingDepthMinMm",
		overrides,
	);

	const { runs } = resolveEffectiveLayout(
		input.layout,
		input.room,
		input.heaterSize,
	);
	const seatingRuns = runs
		.map((entry) => entry.effective)
		.filter((run) => run.tier === "upper");

	let seatingLengthMm = 0;
	let longestRunMm = 0;
	let seats = 0;
	let lyingSpots = 0;

	for (const run of seatingRuns) {
		seatingLengthMm += run.lengthMm;
		longestRunMm = Math.max(longestRunMm, run.lengthMm);
		// Seats are counted PER RUN, not from the pooled length: two 900 mm runs
		// are two seats, not three, because nobody sits across a corner gap.
		seats += Math.floor(run.lengthMm / seatWidthMm);
		if (run.depthMm >= lyingDepthMm) {
			lyingSpots += Math.floor(run.lengthMm / lyingLengthMm);
		}
	}

	return {
		seats,
		lyingSpots,
		seatingLengthMm,
		longestRunMm,
		summaryKey: "domain_interior_capacity_summary",
		args: { seats, lyingSpots },
	};
}
